import { Injectable, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import {
  catchError,
  Observable,
  of,
  Subscription,
  switchMap,
  throwError,
} from 'rxjs';
import type { MultiPolygon, Polygon } from 'geojson';
import { ToastrService } from 'ngx-toastr';
import { ArbitraryCellService } from './arbitrary-cell.service';
import { IsochroneService } from '../../../shared/services/isochrone.service';
import { radiusAreaGeometry } from '../../../shared/util/trade-area-geometry';
import { GoogleAnalyticsService } from '../../../user/google-analytics.service';
import { SearchGeocoderService } from '../../../shared/services/search-geocoder.service';

export type TradeAreaKind = 'drive' | 'radius';

export interface TradeAreaShape {
  kind: TradeAreaKind;
  /** Minutes for a drive-time area, miles for a radius. */
  value: number;
}

export type LngLatPair = [number, number];

export interface TradeAreaRequest {
  center: LngLatPair;
  shape: TradeAreaShape;
}

// Local-business trade areas: a c-store draws from a few minutes, a big-box
// store or a clinic from half an hour. Kept short so the popup stays a single
// dropdown rather than a slider.
export const DRIVE_TIME_PRESETS = [5, 10, 15, 30];
export const RADIUS_PRESETS = [1, 3, 5, 10];

export const DEFAULT_TRADE_AREA_SHAPE: TradeAreaShape = {
  kind: 'drive',
  value: 10,
};

export function tradeAreaLabel({ kind, value }: TradeAreaShape): string {
  return kind === 'drive' ? `${value}-min drive` : `${value}-mi radius`;
}

export function isSameTradeArea(
  a: TradeAreaRequest | null,
  b: TradeAreaRequest | null
): boolean {
  return (
    !!a &&
    !!b &&
    a.shape.kind === b.shape.kind &&
    a.shape.value === b.shape.value &&
    isSamePoint(a.center, b.center)
  );
}

export function isSamePoint(a: LngLatPair, b: LngLatPair): boolean {
  // ~1 m: the same click read back through GL JS, not a different spot.
  const EPSILON = 1e-5;
  return Math.abs(a[0] - b[0]) < EPSILON && Math.abs(a[1] - b[1]) < EPSILON;
}

/** Mapbox had no drive-time polygon for this point - not a transport error. */
class NoDriveTimeAreaError extends Error {}

/** The isochrone request itself failed - kept apart from the backend's 422. */
class DriveTimeRequestError extends Error {}

/**
 * The main map's trade-area analysis: a drive-time or radius area around a
 * point, registered and indexed as an arbitrary cell, then shown in the
 * regular popup. Started from the popup's trade-area row.
 *
 * One analysis at a time. Starting another cancels the one in flight, so the
 * map never ends up showing the popup of an area nobody is waiting for.
 */
@Injectable({
  providedIn: 'root',
})
export class TradeAreaService {
  /** The last shape picked, so the next popup offers the same one. */
  public lastShape: TradeAreaShape = DEFAULT_TRADE_AREA_SHAPE;

  /** The analysis in flight, if any. */
  public readonly pending = signal<TradeAreaRequest | null>(null);

  /** The area currently drawn on the map. */
  public readonly analyzed = signal<TradeAreaRequest | null>(null);

  private analysis?: Subscription;

  constructor(
    private arbitraryCells: ArbitraryCellService,
    private isochrone: IsochroneService,
    private toast: ToastrService,
    private gaService: GoogleAnalyticsService,
    private geocoder: SearchGeocoderService
  ) {}

  public analyze(request: TradeAreaRequest): void {
    this.analysis?.unsubscribe();

    const { center, shape } = request;
    this.lastShape = shape;
    this.pending.set(request);
    this.gaService.logTradeAreaAnalyze(shape.kind, shape.value);
    // The area's own origin pin takes over from the search pin, which is
    // usually on the very same spot and would otherwise sit under it.
    this.geocoder.marker.remove();

    this.analysis = this.buildGeometry(request)
      .pipe(
        switchMap((geometry) => {
          if (!geometry) return throwError(() => new NoDriveTimeAreaError());

          // The previous area comes off the map right here, so a failure
          // before this point leaves it - and `analyzed` - untouched.
          this.analyzed.set(null);
          return this.arbitraryCells.analyzeArea(
            geometry,
            center,
            `${tradeAreaLabel(shape)} trade area`
          );
        })
      )
      .subscribe({
        next: () => this.analyzed.set(request),
        complete: () => this.pending.set(null),
        error: (err) => {
          this.pending.set(null);
          this.reportFailure(err);
        },
      });
  }

  public clear(): void {
    this.analysis?.unsubscribe();
    this.pending.set(null);
    this.analyzed.set(null);
    this.arbitraryCells.resetArbitraryCell();
  }

  private buildGeometry({
    center: [lng, lat],
    shape,
  }: TradeAreaRequest): Observable<Polygon | MultiPolygon | null> {
    // No circle fallback for a drive time, unlike the sales-forecast pages: a
    // straight-line circle presented as a drive-time area is a wrong answer,
    // and here the visitor can simply pick a radius instead.
    return shape.kind === 'drive'
      ? this.isochrone
          .fetchDriveTimeArea(lng, lat, shape.value)
          .pipe(
            catchError((cause) =>
              throwError(
                () => new DriveTimeRequestError('Isochrone failed', { cause })
              )
            )
          )
      : of(radiusAreaGeometry(lng, lat, shape.value));
  }

  private reportFailure(err: unknown): void {
    if (err instanceof NoDriveTimeAreaError) {
      this.toast.warning(
        'Try a radius instead.',
        'No drive-time area for this spot'
      );
      return;
    }

    // The backend's "outside the processed region".
    if (err instanceof HttpErrorResponse && err.status === 422) {
      this.toast.warning('Please try another region', 'No data here');
      return;
    }

    console.error('Trade area analysis failed', err);
    this.toast.error('We couldn’t analyze this area. Please try again.');
  }
}
